import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelpTopicView, tocFor, type TocEntry } from '../../components/help/HelpTopicView';
import { getTopic, HELP_ORDER, HELP_TOPICS } from '../../help/topics';

/**
 * "On this page" outline. Scroll-spy via IntersectionObserver against the
 * viewport (root: null), so it works regardless of which ancestor scrolls.
 */
function HelpToc({ entries }: { entries: TocEntry[] }) {
  const [active, setActive] = useState(entries[0]?.id);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (hits) => {
        const visible = hits
          .filter((h) => h.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      // A band near the top of the viewport counts as "reading here".
      { rootMargin: '-10% 0px -70% 0px' },
    );
    entries.forEach((e) => {
      const el = document.getElementById(e.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [entries]);

  return (
    <nav className="help-toc" aria-label="On this page">
      <div className="help-toc-title">On this page</div>
      <ul>
        {entries.map((e) => (
          <li key={e.id}>
            <a href={`#${e.id}`} className={e.id === active ? 'active' : ''}>
              {e.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function HelpTopicPage() {
  const { topicSlug } = useParams();
  // `key` changes on every navigation, including a repeat of the one we are
  // already on — without it, opening the same search result a second time
  // after scrolling away would leave the page where it was.
  const { hash, key } = useLocation();
  const topic = topicSlug ? getTopic(topicSlug) : undefined;

  // Client-side routing means the browser never resolves the #fragment itself.
  // Help search results link to a specific recipe, section, or glossary term,
  // so the page scrolls there once the topic has rendered. No decoding: these
  // ids come from `headingId`, which emits only [a-z0-9-] — and running a
  // hand-typed "#%" through decodeURIComponent would throw URIError into the
  // render path and blank the page.
  useEffect(() => {
    if (hash === '') return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash, key, topicSlug]);

  if (!topic) return <Navigate to="/help" replace />;

  const toc = tocFor(topic);
  const idx = HELP_ORDER.indexOf(topic.slug);
  const prevSlug = idx > 0 ? HELP_ORDER[idx - 1] : undefined;
  const nextSlug = idx >= 0 && idx < HELP_ORDER.length - 1 ? HELP_ORDER[idx + 1] : undefined;
  const prev = prevSlug ? HELP_TOPICS[prevSlug] : undefined;
  const next = nextSlug ? HELP_TOPICS[nextSlug] : undefined;

  return (
    <div className="container">
      <Link to="/help" className="help-back">
        <ArrowLeft size={14} aria-hidden />
        All help topics
      </Link>

      <div className="help-layout">
        <div>
          <div className="section section--card">
            <HelpTopicView key={topic.slug} topic={topic} />
          </div>

          <nav className="help-pager" aria-label="More topics">
            {prev ? (
              <Link to={`/help/${prev.slug}`} className="help-pager-link">
                <ArrowLeft size={14} aria-hidden />
                <span>
                  <span className="help-pager-eyebrow">Previous</span>
                  {prev.title}
                </span>
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link to={`/help/${next.slug}`} className="help-pager-link help-pager-link--next">
                <span>
                  <span className="help-pager-eyebrow">Next</span>
                  {next.title}
                </span>
                <ArrowRight size={14} aria-hidden />
              </Link>
            ) : (
              <span />
            )}
          </nav>
        </div>

        {toc.length >= 3 && <HelpToc key={topic.slug} entries={toc} />}
      </div>
    </div>
  );
}
